import React, { useState } from 'react';

import { ConditionVue, DossierSkipcarPlus, messageErreur, skipcarPlusActions } from './skipcarPlusApi';
import { LIBELLE_NATURE, LIBELLE_SOURCE_J0, aujourdhuiIso, dateFr, euros, pourcent, tauxDepuisSaisie } from './skipcarPlusFormat';

interface SectionProps {
  dossier: DossierSkipcarPlus;
  rafraichir: () => Promise<unknown>;
}

/** Exécute une action, rafraîchit le dossier, et remonte l'erreur éventuelle. */
const useAction = (rafraichir: () => Promise<unknown>) => {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const executer = async (action: () => Promise<unknown>): Promise<void> => {
    setEnCours(true);
    setErreur(null);
    try {
      await action();
      await rafraichir();
    } catch (error) {
      setErreur(messageErreur(error));
    } finally {
      setEnCours(false);
    }
  };
  return { enCours, erreur, executer };
};

const Carte: React.FC<{ titre: string; aDroite?: React.ReactNode; children: React.ReactNode }> = ({
  titre,
  aDroite,
  children,
}) => (
  <section className="sk-card">
    <header className="sk-card-header">
      <span className="sk-kicker">{titre}</span>
      {aDroite}
    </header>
    <div className="sk-card-body">{children}</div>
  </section>
);

// ── Lot 2 : validations et J0 ────────────────────────────────────────────

const LigneValidation: React.FC<{ libelle: string; validee: boolean; le: string | null; par: string | null }> = ({
  libelle,
  validee,
  le,
  par,
}) => (
  <div className="sp-row">
    <span className={`sp-dot${validee ? ' is-ok' : ''}`} />
    <strong>{libelle}</strong>
    <span className="sp-muted">{validee ? `le ${dateFr(le)}${par ? ` par ${par}` : ''}` : 'en attente'}</span>
  </div>
);

export const ContratSection: React.FC<SectionProps> = ({ dossier, rafraichir }) => {
  const { enCours, erreur, executer } = useAction(rafraichir);
  const [previsionnel, setPrevisionnel] = useState(dossier.j0.previsionnel ?? '');
  const [saisi, setSaisi] = useState(dossier.j0.saisi ?? '');
  const { j0, validations } = dossier;

  return (
    <Carte titre="Validation et date de reprise">
      <div className="sp-stack">
        <div>
          <LigneValidation libelle="Validation client" {...validations.client} />
          <LigneValidation libelle="Validation Skipcar" {...validations.skipcar} />
          {validations.skipcar.validee && !validations.client.validee && (
            <p className="sp-muted">Validé par Skipcar seul, avec le client au téléphone.</p>
          )}
        </div>

        <div>
          <div className="sk-label">J0 retenu</div>
          <div className="sp-indicator-value">{j0.date ? dateFr(j0.date) : 'À venir'}</div>
          <div className="sp-muted">
            {j0.source ? LIBELLE_SOURCE_J0[j0.source] : 'Ni date de conformité, ni déclaration d’achat'}
            {j0.estime && j0.date ? ' : estimation, remplacée par la date d’achat à la DA' : ''}
          </div>
        </div>

        <div className="sp-inline-form">
          <label>
            <span className="sk-label">Date du dossier de conformité (J0 prévisionnel)</span>
            <input className="sk-input" type="date" value={previsionnel} onChange={(e) => setPrevisionnel(e.target.value)} />
          </label>
          <button
            type="button"
            className="sk-btn sk-btn-secondary"
            disabled={enCours || previsionnel === (dossier.j0.previsionnel ?? '')}
            onClick={() => executer(() => skipcarPlusActions.setJ0Previsionnel(dossier.dealId, previsionnel || null))}
          >
            Enregistrer
          </button>
        </div>

        {dossier.vueDieu && (
          <div className="sp-inline-form">
            <label>
              <span className="sk-label">
                J0 effectif (prime sur la DA{dossier.j0.dateDa ? ` du ${dateFr(dossier.j0.dateDa)}` : ''})
              </span>
              <input className="sk-input" type="date" value={saisi} onChange={(e) => setSaisi(e.target.value)} />
            </label>
            <button
              type="button"
              className="sk-btn sk-btn-secondary"
              disabled={enCours || saisi === (dossier.j0.saisi ?? '')}
              onClick={() => executer(() => skipcarPlusActions.setJ0Effectif(dossier.dealId, saisi || null))}
            >
              Enregistrer
            </button>
            {dossier.j0.saisi && (
              <button
                type="button"
                className="sk-btn sk-btn-ghost"
                disabled={enCours}
                onClick={() => executer(() => skipcarPlusActions.setJ0Effectif(dossier.dealId, null))}
              >
                Revenir à la date de la DA
              </button>
            )}
          </div>
        )}
        {erreur && <p className="sp-error">{erreur}</p>}
      </div>
    </Carte>
  );
};

// ── Lot 3 : conditions d'éligibilité ─────────────────────────────────────

const TuileCondition: React.FC<{
  condition: ConditionVue;
  dossier: DossierSkipcarPlus;
  executer: (action: () => Promise<unknown>) => Promise<void>;
  enCours: boolean;
}> = ({ condition, dossier, executer, enCours }) => {
  const [montantHt, setMontantHt] = useState((dossier.montants.participationHtCentimes / 100).toFixed(2));
  const sansObjet = condition.nature === 'sans_objet';
  const classe = sansObjet ? 'is-na' : condition.validee ? 'is-done' : 'is-next';
  const estManuelle = condition.cle === 'contrat' || condition.cle === 'caution';
  const facture = dossier.factureFraisAchat;
  const factureAEmettre = condition.cle === 'frais_achat' && !sansObjet && facture === null;
  const factureNonConfirmee = condition.cle === 'frais_achat' && facture?.statut === 'pending';
  // Payée chez Qonto mais pas encore vue par la synchro générale : on la relit à la demande.
  const factureAPayer = condition.cle === 'frais_achat' && facture?.statut === 'unpaid';
  const montantSaisi = Number(montantHt.replace(',', '.'));

  return (
    <div className={`sp-tile ${classe}`}>
      <div className="sp-tile-title">
        {!sansObjet && <span className={`sp-dot${condition.validee ? ' is-ok' : ''}`} />}
        {condition.libelle}
      </div>
      <div className="sp-tile-result">{condition.detail}</div>
      <div className="sp-tile-meta">{LIBELLE_NATURE[condition.nature]}</div>

      {condition.cle === 'mandat' && !condition.validee && (
        <a className="sp-tile-meta" href={dossier.mandat.lienSignature} target="_blank" rel="noreferrer">
          Lien de signature du mandat Qonto
        </a>
      )}
      {condition.cle === 'mandat' && (
        <div className="sp-tile-meta">
          {dossier.mandat.fraisRejetHtEuros} € HT de frais de dossier en cas de rejet de prélèvement
        </div>
      )}

      {dossier.vueDieu && estManuelle && (
        <button
          type="button"
          className="sk-btn sk-btn-ghost"
          disabled={enCours}
          onClick={() =>
            executer(() =>
              skipcarPlusActions.setCondition(dossier.dealId, condition.cle as 'contrat' | 'caution', !condition.validee),
            )
          }
        >
          {condition.validee ? 'Décocher' : 'Cocher'}
        </button>
      )}

      {dossier.vueDieu && factureAEmettre && (
        <div className="sp-inline-form">
          <label>
            <span className="sk-label">Montant HT</span>
            <input className="sk-input" value={montantHt} onChange={(e) => setMontantHt(e.target.value)} />
          </label>
          <button
            type="button"
            className="sk-btn sk-btn-secondary"
            disabled={enCours}
            onClick={() => {
              // Vraie facture Qonto, numérotée, envoyée à l'agence : on confirme d'abord.
              const confirme = window.confirm(
                `Émettre la facture de frais d'achat de ${montantSaisi.toLocaleString('fr-FR')} € HT et l'envoyer à ${dossier.agence.nom ?? "l'agence"} ?`,
              );
              if (confirme) executer(() => skipcarPlusActions.emettreFactureFraisAchat(dossier.dealId, montantSaisi));
            }}
          >
            Émettre la facture
          </button>
        </div>
      )}

      {dossier.vueDieu && factureAPayer && (
        <button
          type="button"
          className="sk-btn sk-btn-ghost"
          disabled={enCours}
          onClick={() => executer(() => skipcarPlusActions.synchroniserFactureFraisAchat(dossier.dealId))}
        >
          Synchroniser avec Qonto
        </button>
      )}

      {dossier.vueDieu && factureNonConfirmee && (
        <button
          type="button"
          className="sk-btn sk-btn-ghost"
          disabled={enCours}
          onClick={() => {
            const confirme = window.confirm(
              `Qonto n'a jamais confirmé la facture ${facture?.numero ?? ''}. Vérifiez qu'elle n'existe pas dans Qonto avant de l'annuler ici.`,
            );
            if (confirme) executer(() => skipcarPlusActions.annulerFactureEnAttente(dossier.dealId));
          }}
        >
          Absente de Qonto : annuler
        </button>
      )}
    </div>
  );
};

export const ConditionsSection: React.FC<SectionProps> = ({ dossier, rafraichir }) => {
  const { enCours, erreur, executer } = useAction(rafraichir);
  const { validees, total } = dossier.bilanConditions;
  return (
    <Carte
      titre="Conditions d'éligibilité"
      aDroite={
        <span className={`sk-badge ${validees === total ? 'sk-badge-success' : 'sk-badge-neutral'}`}>
          {validees}/{total} validées
        </span>
      }
    >
      <div className="sp-tiles is-wide">
        {dossier.conditions.map((condition) => (
          <TuileCondition key={condition.cle} condition={condition} dossier={dossier} executer={executer} enCours={enCours} />
        ))}
      </div>
      {!dossier.vueDieu && <p className="sp-muted">Le contrat et la caution sont cochés par Skipcar.</p>}
      {erreur && <p className="sp-error">{erreur}</p>}
    </Carte>
  );
};

// ── Lot 4 : processus d'achat ────────────────────────────────────────────

const CLASSE_ETAPE = { fait: 'is-done', prochaine: 'is-next', attente: 'is-wait' } as const;

export const ProcessusAchatSection: React.FC<{ dossier: DossierSkipcarPlus }> = ({ dossier }) => {
  const faites = dossier.processusAchat.filter((etape) => etape.etat === 'fait').length;
  return (
    <Carte
      titre="Processus d'achat"
      aDroite={
        <span className="sp-muted">
          {faites}/{dossier.processusAchat.length} étapes · chaque action devient son résultat
        </span>
      }
    >
      <div className="sp-tiles">
        {dossier.processusAchat.map((etape) => (
          <div key={etape.cle} className={`sp-tile ${CLASSE_ETAPE[etape.etat]}`}>
            <div className="sp-tile-title">{etape.libelle}</div>
            <div className="sp-tile-result">{etape.resultat ?? (etape.etat === 'prochaine' ? 'À faire' : 'En attente')}</div>
          </div>
        ))}
      </div>
      {dossier.vueDieu && (
        <p className="sp-muted">
          Les actions (cession, DA, livre de police) restent celles du pipeline d’achat, dans la liste des deals.
        </p>
      )}
    </Carte>
  );
};

// ── Lot 1 : grille de taux du dossier ────────────────────────────────────

const LIBELLES_PALIERS = ['Sem. 1 à 4', 'Sem. 5 à 8', 'Sem. 9 à 12', 'Sem. 13 à 52'];

/** Saisie d'une dérogation, dans une fenêtre : les taux ne sont jamais éditables en permanence sur la fiche. */
const FenetreDerogation: React.FC<{
  dossier: DossierSkipcarPlus;
  fermer: () => void;
  executer: (action: () => Promise<unknown>) => Promise<void>;
  enCours: boolean;
}> = ({ dossier, fermer, executer, enCours }) => {
  const [saisies, setSaisies] = useState(dossier.grille.taux.map((taux) => (taux * 100).toString().replace('.', ',')));
  const grilleSaisie = saisies.map(tauxDepuisSaisie);
  const saisieValide = grilleSaisie.every((taux) => taux !== null);

  return (
    <div className="sp-modal-backdrop" role="dialog" aria-modal="true" aria-label="Poser une dérogation de taux" onClick={fermer}>
      <section className="sk-card sp-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sk-card-header">
          <span className="sk-kicker">Dérogation de taux · {dossier.vehicule.immatriculation ?? dossier.dealId}</span>
        </header>
        <div className="sk-card-body">
          <p className="sp-muted">
            La dérogation s’applique aux semaines à venir. Les semaines déjà facturées gardent leur taux.
          </p>
          <div className="sp-inline-form">
            {saisies.map((saisie, index) => (
              <label key={LIBELLES_PALIERS[index]}>
                <span className="sk-label">{LIBELLES_PALIERS[index]} (%)</span>
                <input
                  className="sk-input"
                  value={saisie}
                  onChange={(e) => setSaisies(saisies.map((valeur, i) => (i === index ? e.target.value : valeur)))}
                />
              </label>
            ))}
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sk-btn sk-btn-ghost" onClick={fermer}>
              Annuler
            </button>
            <button
              type="button"
              className="sk-btn sk-btn-primary"
              disabled={enCours || !saisieValide}
              onClick={async () => {
                await executer(() => skipcarPlusActions.deroger(dossier.dealId, grilleSaisie as number[]));
                fermer();
              }}
            >
              Enregistrer la dérogation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export const GrilleSection: React.FC<SectionProps> = ({ dossier, rafraichir }) => {
  const { enCours, erreur, executer } = useAction(rafraichir);
  const [fenetreOuverte, setFenetreOuverte] = useState(false);

  return (
    <Carte
      titre="Grille de taux"
      aDroite={
        <span className={`sk-badge ${dossier.grille.derogation || !dossier.grille.figee ? 'sk-badge-warning' : 'sk-badge-pending'}`}>
          {!dossier.grille.figee
            ? 'Contrat antérieur : taux à reprendre'
            : dossier.grille.derogation
              ? 'Dérogation'
              : `Profil ${dossier.grille.profilNom ?? 'Standard'}`}
        </span>
      }
    >
      <div className="sp-rates">
        {dossier.grille.taux.map((taux, index) => (
          <div key={LIBELLES_PALIERS[index]}>
            <div className="sk-label">{LIBELLES_PALIERS[index]}</div>
            <strong>{pourcent(taux)}</strong>
          </div>
        ))}
      </div>
      <p className="sp-muted">
        Taux figés sur le dossier : un changement de profil de l’agence ne les modifie pas. Prélèvements{' '}
        {dossier.grille.rythme === 'terme_echu' ? 'à terme échu, sauf le premier' : 'd’avance (contrat repris)'}.
      </p>
      {dossier.vueDieu && (
        <button type="button" className="sk-btn sk-btn-secondary" onClick={() => setFenetreOuverte(true)}>
          Poser la dérogation
        </button>
      )}
      {fenetreOuverte && (
        <FenetreDerogation dossier={dossier} fermer={() => setFenetreOuverte(false)} executer={executer} enCours={enCours} />
      )}
      {erreur && <p className="sp-error">{erreur}</p>}
    </Carte>
  );
};

// ── Échéancier (calculé ; facture et prélèvement réels arrivent avec le lot 5) ──

export const EcheancierSection: React.FC<{ dossier: DossierSkipcarPlus }> = ({ dossier }) => {
  const aujourdhui = aujourdhuiIso();
  const prochaine = dossier.echeancier.find((bloc) => bloc.datePrelevement >= aujourdhui);
  const blocs = dossier.vueDieu
    ? dossier.echeancier
    : dossier.echeancier.filter((bloc) => bloc.datePrelevement >= aujourdhui).slice(0, 3);
  const passes = dossier.echeancier.filter((bloc) => bloc.datePrelevement < aujourdhui).length;
  return (
    <Carte
      titre={dossier.vueDieu ? 'Échéancier sur 52 semaines' : 'Prochaines échéances'}
      aDroite={
        dossier.vueDieu && dossier.echeancier.length > 0 ? (
          <span className="sp-muted">
            {passes} passée{passes > 1 ? 's' : ''} · {dossier.echeancier.length - passes} à venir
          </span>
        ) : undefined
      }
    >
      {!dossier.grille.figee ? (
        <p className="sp-muted">
          Contrat antérieur à l’outil : son échéancier s’affichera après la reprise, avec ses taux et son rythme réels.
        </p>
      ) : dossier.echeancier.length === 0 ? (
        <p className="sp-muted">L’échéancier se calcule dès que le J0 est connu.</p>
      ) : (
        <div className="sp-table-wrap">
          <table className="sk-table">
            <thead>
              <tr>
                <th>Prélèvement</th>
                <th>Date</th>
                <th>Couvre</th>
                <th className="sk-amount">HT</th>
                <th className="sk-amount">TTC</th>
                <th>Facture</th>
                <th>Prélèvement</th>
              </tr>
            </thead>
            <tbody>
              {blocs.map((bloc) => {
                const passe = bloc.datePrelevement < aujourdhui;
                const estProchaine = bloc === prochaine;
                return (
                  <tr key={bloc.numero} className={passe ? 'is-past' : estProchaine ? 'is-next' : undefined}>
                    <td>{bloc.numero}</td>
                    <td>{dateFr(bloc.datePrelevement)}</td>
                    <td>
                      semaines {bloc.premiereSemaine} et {bloc.derniereSemaine}
                    </td>
                    <td className="sk-amount">{euros(bloc.montantHtCentimes)}</td>
                    <td className="sk-amount">{euros(bloc.montantTtcCentimes)}</td>
                    <td className="sp-muted">—</td>
                    <td>{passe ? 'Échue' : estProchaine ? 'Prochaine' : 'À venir'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dossier.j0.estime && dossier.echeancier.length > 0 && (
        <p className="sp-muted">Dates estimées sur le J0 prévisionnel : elles se recalent à la déclaration d’achat.</p>
      )}
      {dossier.vueDieu && dossier.echeancier.length > 0 && (
        <p className="sp-muted">Le numéro de facture et le statut réel du prélèvement s’afficheront avec les prélèvements automatiques.</p>
      )}
    </Carte>
  );
};

export const HistoriqueSection: React.FC<{ dossier: DossierSkipcarPlus }> = ({ dossier }) =>
  dossier.vueDieu && dossier.historique.length > 0 ? (
    <Carte titre="Historique">
      <table className="sk-table">
        <tbody>
          {dossier.historique.map((evenement) => (
            <tr key={`${evenement.created_at}-${evenement.event}`}>
              <td>{dateFr(evenement.created_at)}</td>
              <td>{evenement.event.replace(/_/g, ' ')}</td>
              <td className="sp-muted">{evenement.author ?? 'automatique'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Carte>
  ) : null;
